import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { onAppEvent } from '@/src/renderer/lib/events'
import { consumePendingQuickNotesJump } from '@/src/renderer/lib/taskJump/pendingTaskJump'
import { whenTaskRowSettled } from '@/src/renderer/lib/taskJump/whenTaskRowSettled'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, PresenceContext } from 'framer-motion'
import { useDataSource } from '../../../../lib/dataSource'
import type { TaskListHandle } from '../../components/TaskList'
import type { Task } from '../../../../lib/db/models'
import { TaskBoard } from '../../components/TaskBoard'
import { TitleRecognitionScope } from '../../components/TaskEntry'
import { useProjectNavigation } from '../../hooks/useProjectNavigation'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useTaskCreation } from '../../hooks/useTaskCreation'
import { useTaskDragAndDrop } from '../../hooks/useTaskDragAndDrop'
import { useNestActions } from '../../hooks/useNestActions'
import { useNestMenu } from '../../hooks/useNestMenu'
import { useKeyboardMove } from '../../hooks/useKeyboardMove'
import { usePendingSelection } from '../../hooks/usePendingSelection'
import { useSectionDrag, type OptimisticSectionUpdates } from '../../hooks/useSectionDrag'
import { useSectionDragAnimation } from '../../hooks/useSectionDragAnimation'
import { useProjectKeyboardShortcuts } from '../../hooks/useProjectKeyboardShortcuts'
import { useDuplicateItems } from '../../hooks/useDuplicateItems'
import { useContextMenuMarks } from '../../hooks/useContextMenuMarks'
import { useTaskMenuShortcuts, anchorTaskId } from '../../hooks/useTaskMenuShortcuts'
import { useAppTaskMenu, menuAnchorOf } from '../../hooks/useAppTaskMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { useMoveMenu } from '../../hooks/useMoveMenu'
import { useTaskUpdates } from '../../hooks/useTaskUpdates'
import { useSelectionActions } from '../../hooks/useSelectionActions'
import { useFavoriteShortcut } from '../../hooks/useFavoriteShortcut'
import type { TaskMenuTarget } from '../../components/TaskMenu/TaskMenu.types'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import { useOptimisticOrder } from '../../hooks/useOptimisticOrder'
import { shownQuickNotes } from './utils/shownQuickNotes'
import { useDndComposition } from '../ProjectPage/hooks/useDndComposition'
import { isListToolbarRetracted } from '../ProjectPage/utils/isListToolbarRetracted'
import { quickNotesNewTaskTarget } from './utils/quickNotesNewTaskTarget'
import { ListToolbarDock, LIST_TOOLBAR_CLEARANCE } from '../../components/ListToolbarDock'
import { Combine } from 'lucide-react'
import { QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../../components/icons/QuickNotesIcon'
import { ShortcutHint } from '../../../../components/ui/ShortcutHint'
import '../../../../components/ui/Button/Button.scss'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { usePendingRestore } from '../../hooks/usePendingRestore'
import { splitCompleted } from '../../utils/completedGate'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import { CompletedToggle } from '../../components/CompletedToggle'
import { activeQuickNotes } from './utils/activeQuickNotes'
import { useQuickNotesSegments, QUICK_NOTES_SECTION_IDS } from './hooks/useQuickNotesSegments'
import { useNewTaskDrag } from '../../hooks/useNewTaskDrag'
import { applyNewTaskDrop, type NewTaskDropTarget } from '../../utils/newTaskDropTarget'
import { NewTaskDragGhost } from '../../components/NewTaskDrag'
import { useInboxDepartures } from './hooks/useInboxDepartures'
import { InboxMovedNotice } from './components/InboxMovedNotice'
import { AutoHeight } from '@/src/renderer/components/ui/AutoHeight'
import { useQuickNotesCollapsedState } from './hooks/useQuickNotesCollapsedState'
import { collapsedSegmentOf } from '../../utils/revealCollapsed'
import { useClarifyLayoutActive } from './hooks/useClarifyLayoutActive'
import { useLocation } from 'react-router-dom'
import { useQuickNotesClarify } from './hooks/useQuickNotesClarify'
import { useScopedNavState } from '../../hooks/useScopedNavState'
import { useScrollFades } from '../../hooks/useScrollFades'
import { usePageBreadcrumb } from '../../hooks/usePageBreadcrumb'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { ClarifySession } from './ClarifyView'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { TaskDetails, DetailsToggle } from '../../components/TaskDetails'
import { EmptyStateGlow } from '../../components/EmptyState'
import { IntroOfferCard } from '../../intro/IntroOfferCard'
import { useIntroOffer } from '@/src/renderer/lib/intro'
import { QuickNotesEmptyState, useEmptyAppActions } from './QuickNotesEmptyState'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useTaskDetailsPane, type UseTaskDetailsPane } from '../../hooks/useTaskDetailsPane'
import { TaskDetailsOpenerProvider } from '../../hooks/TaskDetailsOpener'
import { topVisibleTaskId } from '../../hooks/useTopVisibleTask'
import '../TaskViews.scss'
import '../ProjectPage/ProjectPage.scss'
import './QuickNotesPage.scss'
import { usePlayTask } from '@/src/renderer/lib/focus'

interface QuickNotesPageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

export const QuickNotesPage: React.FC<QuickNotesPageProps> = ({ lifeSpace }) => {
  const isActive = useIsActiveTab()
  const { t } = useTranslation()

  // ── Data: orphan tasks for the current lifespace ──
  // Kurz-notiert is now a virtual queue of orphan tasks
  // (projectId == null && lifeSpace == X). Previously this was a real
  // system project per lifespace — see PRs 1 + 2 of the Task.lifeSpace
  // migration. Sections are still virtual (useQuickNotesSegments groups
  // by age). Unchanged tasks keep their reference (see `DataSource`), so
  // tab-switch re-subscriptions bail.
  const tasks = useDataSource().useQuickNotes(lifeSpace)
  // Ist HappyTask ganz leer: „Ich habe ein Backup …“ und „Aus anderer App importieren …“ (#158).
  const emptyAppActions = useEmptyAppActions()
  const introOffer = useIntroOffer()
  const noSections = useMemo(() => [], [])

  // Die offenen Einträge — ohne ruhende, ohne erledigte. Die Regel samt ihren
  // zwei Gründen steht in `activeQuickNotes`; der rohe `tasks` bleibt für den
  // Abgleich der optimistischen Überschreibungen unten.
  const activeTasks = useMemo(() => activeQuickNotes(tasks), [tasks])

  // ── Clarify mode ("Einträge klären") ──
  const { clarifyMode, clarifyTasks, enterClarify, exitClarify, toggleClarify } =
    useQuickNotesClarify(lifeSpace, activeTasks)
  // Layout class has to stay applied through the AnimatePresence exit fade —
  // otherwise the inner scroll list re-flows mid-fade and visibly "fans out".
  const { layoutActive: clarifyLayoutActive, handleExitComplete: handleClarifyExitComplete } =
    useClarifyLayoutActive(clarifyMode)

  // Global "K" toggles clarify mode from list view; inside ClarifyView, K is
  // handled by its own keydown listener (commit + exit). Gated on `isActive`
  // so inactive-but-mounted tabs don't steal the shortcut.
  useEffect(() => {
    if (!isActive) return
    if (clarifyMode) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return
      }
      if (e.key === 'k' || e.key === 'K') {
        if (!activeTasks || activeTasks.length === 0) return
        e.preventDefault()
        enterClarify()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isActive, clarifyMode, activeTasks, enterClarify])

  // ── Collapsed state for virtual sections (persisted per lifespace) ──
  const { isCollapsed, toggleCollapsed } = useQuickNotesCollapsedState(lifeSpace)

  // ── Optimistic per-task field overrides ──
  // See ProjectPage for full rationale — every mutation handler routes
  // through this so user actions paint in the next frame instead of
  // waiting on the IDB write + LiveQuery refire (~1–2s when sync push/
  // pull cycles compete for the main thread).
  // QuickNotes uses *virtual* sections only (no Dexie row), so no
  // section-override map is wired here — virtual section state lives in
  // local React state already (`useQuickNotesCollapsedState`).
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(tasks)

  // Hier oben, weil der Liegezeit-Hook ihn liest und `effectiveTasks` gleich
  // darunter davon abhängt. Gesetzt wird er weiter unten, sobald die
  // Ziehgesten stehen.
  const isDragAnimatingRef = useRef(false)

  // ── Der Geschafft-Schalter ──
  // Zwei Sekunden **Liegezeit** am Platz, danach hinter den Schalter am Fuß der
  // Liste. `hold` friert das ein, solange gezogen wird.
  const { graceIds, markCompleted } = useCompletionGrace({ hold: isDragAnimatingRef.current })
  const [completedOpen, setCompletedOpen] = useState(false)

  // Vorläufige Reihenfolge nach Ziehen und ⌥↑/↓, bis die Datenbank sie bestätigt.
  const [optimisticUpdates, setOptimisticUpdates] = useOptimisticOrder(tasks)

  // Apply overrides on top of the raw task list. Used for both the
  // visible segments AND every downstream consumer that derives state
  // from tasks (creation, drag, navigation) so they all see the same
  // optimistic snapshot until Dexie confirms.
  // Paused tasks (ruhend) leave the Kurz-notiert list via the Clarify
  // "Ruhen lassen"-Aktion (`pausedAt = now`) and appear in Ruhend instead.
  const sichtbareQuelle = useMemo(
    () => shownQuickNotes(tasks, optimisticTaskOverrides, optimisticUpdates),
    [tasks, optimisticTaskOverrides, optimisticUpdates],
  )

  /*
    **Was Kurz notiert verlässt, steht in der Meldung** (Abnahme F-KN3). Ein
    Startdatum nimmt die Notiz aus `isQuickNote`, die Abfrage liefert sie also
    nicht mehr — und das ist hier richtig so: Die Liste zeigt das Ergebnis, die
    Meldung darüber sagt, was gegangen ist, mit Namen.
  */
  const { held, noteUpdate, noteUnnested, confirm: confirmMoved, updateHeld } = useInboxDepartures(tasks, isActive)
  const effectiveTasks = sichtbareQuelle

  // Was in der Liste steht und was hinter dem Schalter liegt. `effectiveTasks`
  // bleibt ganz: `useTaskOperations` muss die verborgene Aufgabe finden, wenn
  // im Haufen der Haken herausgenommen wird.
  const { visible: visibleTasks, completed: completedTasks } = useMemo(
    () => splitCompleted(effectiveTasks, graceIds),
    [effectiveTasks, graceIds],
  )

  // ── Virtual segments: "Letzte 7 Tage" / "Älter" ──
  const segments = useQuickNotesSegments(
    visibleTasks,
    t('quickNotes.sectionRecent'),
    t('quickNotes.sectionOlder'),
    isCollapsed,
  )

  // ── Local UI state ──
  const [optimisticSectionUpdates, setOptimisticSectionUpdates] = useState<OptimisticSectionUpdates>(null)
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)
  const [lastInteractedSegmentIndex, setLastInteractedSegmentIndex] = useState(0)

  // Refs
  const pageRef = useRef<HTMLDivElement>(null)
  // Die Spalte: fester Rahmen um den Scroller, trägt die Verläufe und die
  // Listen-Werkzeug-Pille — die misst an ihr, wie viel Platz sie hat.
  const listColumnRef = useRef<HTMLDivElement>(null)
  /*
    **Der Scroller** — derselbe wie im Projekt (`project-page__body-main`, #273).
    Bis 06.10.2026 rollte die Spalte selbst; ein Verlaufsband daran wäre mit dem
    Inhalt weggerollt, Kurz notiert hatte darum keins, und der angeheftete
    Abschnittskopf stand ohne Deckung über den Aufgaben.
  */
  const bodyMainRef = useRef<HTMLDivElement>(null)
  // Die **Bildlaufleiste** (CONTEXT.md) wie im Projekt: unsichtbar in Ruhe,
  // sichtbar beim Rollen.
  const { visible: scrollbarVisible, onScroll: handleListScroll } =
    useScrollbarVisibility({ ref: bodyMainRef })
  useScrollFades(bodyMainRef, listColumnRef)
  const titleRef = useRef<HTMLHeadingElement>(null)
  /** Der Griff entsteht erst nach der Navigation — was vorher danach fragt, geht über den Ref. */
  const detailsRef = useRef<UseTaskDetailsPane | null>(null)
  const taskListRef = useRef<HTMLDivElement>(null)
  const taskListHandleRef = useRef<TaskListHandle | null>(null)
  /** **Play** — beginnt einen Fokus an dieser Aufgabe (Zeile, Menü). */
  const handlePlayTask = usePlayTask()
  const segmentHandleRefs = useRef(new Map<string, { current: TaskListHandle | null }>())
  const navFocusFirstRef = useRef<(() => void) | null>(null)
  const navClearSelectionRef = useRef<(() => void) | null>(null)
  const navSelectTaskRef = useRef<((taskId: string) => boolean) | null>(null)
  const navSelectSectionRef = useRef<((sectionId: string) => void) | null>(null)
  const navFocusedItemRef = useRef<{ type: 'task' | 'section'; id: string } | null>(null)
  const activeSegmentRef = useRef(0)

  const getSegmentHandleRef = useCallback((key: string) => {
    const map = segmentHandleRefs.current
    if (!map.has(key)) map.set(key, { current: null })
    return map.get(key)!
  }, [])

  // ── Task operations ──
  // Wired with the optimistic-override sink. Pass `effectiveTasks`
  // (override-applied) so a rapid second click reads the *intended*
  // status — not the still-stale raw Dexie row.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // Jeder Weg zum Haken läuft hier durch — Klick aufs Kästchen und
  // `Leertaste` auf der Auswahl.
  const toggleCompleteWithGrace = useCallback((ids: Set<string>) => {
    markCompleted(ids)
    handleToggleComplete(ids)
  }, [markCompleted, handleToggleComplete])

  // ── Task creation ──
  const {
    creatingPosition, creatingAfterTaskId, creatingSegmentIdx, pendingInlineTarget, morphData,
    positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey, creationKey, landed: creationLanded,
    inlineResetKey, skipInlineEntrance, autoSelectId,
    startCreating, startCreatingAfter,
    handleCreateTask, handleCreateTaskAndContinue,
    handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
    handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
    topCreateRef, bottomCreateRef,
  } = useTaskCreation({
    projectId: undefined, lifeSpace, segments, tasks, sections: noSections, virtualSections: true,
    activeSegmentRef, navClearSelectionRef, navSelectTaskRef, navSelectSectionRef, navFocusedItemRef,
    setLastInteractedSegmentIndex,
  })

  // ── Cmd/Ctrl+Enter in expanded mode: save edit + open creator right after task ──
  const handleUpdateTaskAndCreateBelow = useCallback(async (taskId: string, data: Parameters<typeof handleUpdateTask>[1]) => {
    await handleUpdateTask(taskId, data)
    startCreatingAfter(taskId)
  }, [handleUpdateTask, startCreatingAfter])

  /*
    Wie im **Projekt**: Was hinter dem **Geschafft-Schalter** liegt, gehört zur
    Navigation, solange er offen ist — die Zeilen dort sind gewöhnliche Aufgabenzeilen.
  */
  const navSegments = useMemo(
    () => (completedOpen && completedTasks.length > 0
      ? [...segments, { section: null, tasks: completedTasks, taskCount: completedTasks.length }]
      : segments),
    [segments, completedOpen, completedTasks],
  )

  // ── Navigation ──
  // ⌥↑/↓ und ⇧⌥↑/↓ (#204): Die Navigation entsteht vor den Wegen, die sie selbst braucht
  // (Aufklappen, Umsortieren) — deshalb über Refs, gesetzt weiter unten.
  const moveSelectionRef = useRef<((ids: string[], direction: -1 | 1) => void) | null>(null)
  const nestSelectionRef = useRef<((ids: string[], parentId: string, direction: -1 | 1) => void) | null>(null)
  const handleMoveSelection = useCallback((ids: string[], direction: -1 | 1) => moveSelectionRef.current?.(ids, direction), [])
  const handleNestSelection = useCallback((ids: string[], parentId: string, direction: -1 | 1) => nestSelectionRef.current?.(ids, parentId, direction), [])
  const navigation = useProjectNavigation({
    onMoveSelection: handleMoveSelection,
    onNestSelection: handleNestSelection,
    segments: navSegments,
    onEscapeTop: () => pageRef.current?.focus(),
    onDelete: handleDeleteTask,
    onDeleteSection: () => { /* no-op: virtual sections can't be deleted */ },
    onToggleComplete: toggleCompleteWithGrace,
    onToggleCollapse: toggleCollapsed,
    onCreateBelow: startCreatingAfter,
    onCreateInSection: () => { /* no-op for virtual sections */ },
    isDragAnimatingRef,
    skipFocusCorrectionRef: { current: null },
    sectionsReadonly: true,
    isActive,
    /*
      **Bei offenen Aufgaben-Details klappt nichts mehr auf** (CONTEXT.md):
      Die Aufgabe steht dort vollständig, und die aufgeklappte Karte hielte
      Titel und Notizen als zweiten Entwurf desselben Satzes. Der Doppelklick
      setzt stattdessen den Caret in den Titel der Details.
    */
    suppressExpand: () => detailsRef.current?.open ?? false,
    onSuppressedExpand: () => detailsRef.current?.signalFocusTitle(),
  })
  const { clearSelection: navClearSelection, selectTask: navSelectTask } = navigation
  navFocusFirstRef.current = navigation.focusFirst
  navClearSelectionRef.current = navClearSelection
  navSelectTaskRef.current = navSelectTask
  navSelectSectionRef.current = navigation.selectSection

  /*
    **Die Aufgaben-Details** (Abnahme 21.09.2026, F-KN4): Es gab sie nur im
    Projekt — „muss hier unbedingt rein" (Nutzer). Derselbe Griff, dieselbe
    Gestalt; nur die Pinnwand fehlt, die gibt es hier nicht.

    **Die oberste sichtbare Zeile misst der Scroller**, nicht die Seite: Gegen
    die Seite gemessen wäre jede Zeile „sichtbar" und der Knopf nähme immer die
    erste.
  */
  const details = useTaskDetailsPane({
    tasks: effectiveTasks,
    navigation: {
      selectedTaskIds: navigation.selectedIds,
      selectTask: navigation.selectTask,
      stepTask: navigation.stepToAdjacentTask,
      canStep: direction => navigation.adjacentTaskIndex(direction) !== -1,
      focusList: () => navigation.wrapperRef.current?.focus({ preventScroll: true }),
      topVisibleTaskId: () => {
        const scroller = bodyMainRef.current
        return scroller ? topVisibleTaskId(scroller) : null
      },
    },
    setOptimisticTaskOverride,
  })
  detailsRef.current = details

  /*
    **Der Sprung aus „Favoriten“** (#210): eine Aufgabe ohne Projekt landet hier. Wie der Weg aus
    dem Geschafft-Schalter: je Bild versuchen, bis die Zeile dasteht (höchstens eine Sekunde), dann
    ins Bild holen und die Details auf. Eine erledigte liegt hinter dem Schalter — der klappt auf.
    Das Ereignis holt den Sprung auch ab, wenn die Seite schon offen steht.
  */
  const [jumpSignal, setJumpSignal] = useState(0)
  useEffect(() => onAppEvent('pending-task-jump', () => setJumpSignal(n => n + 1)), [])
  const effectiveTasksRef = useRef(effectiveTasks)
  effectiveTasksRef.current = effectiveTasks
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  const quickNotesLoaded = effectiveTasks !== undefined
  useEffect(() => {
    if (!isActive || !quickNotesLoaded) return
    const pending = consumePendingQuickNotesJump(lifeSpace)
    if (!pending) return
    if (effectiveTasksRef.current?.find(task => task.id === pending.taskId)?.status === 'completed') setCompletedOpen(true)
    // Zugeklappt steht sie nicht in der Navigation — das Zeitfenster aufklappen.
    const collapsedSegment = collapsedSegmentOf(pending.taskId, segmentsRef.current)
    if (collapsedSegment) toggleCollapsed(collapsedSegment)
    // Erst wenn die Seite fertig geladen ist — sonst rutscht die Aufgabe beim Nachladen wieder weg.
    return whenTaskRowSettled({
      root: () => pageRef.current,
      taskId: pending.taskId,
      run: row => {
        if (!navSelectTaskRef.current?.(pending.taskId)) return
        row.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (pending.openDetails) detailsRef.current?.show()
      },
    })
  }, [isActive, quickNotesLoaded, lifeSpace, jumpSignal, toggleCollapsed])

  // Eine bereits offene Karte klappt zu, sobald die Aufgaben-Details aufgehen:
  // Zwei offene Editoren wären zwei Entwürfe desselben Satzes.
  const { setEditingTaskId: navSetEditingTaskId } = navigation
  useEffect(() => {
    if (details.open) navSetEditingTaskId(null)
  }, [details.open, navSetEditingTaskId])

  /**
   * Der Rückweg aus dem **Geschafft-Schalter**: Haken heraus, und die Notiz
   * steht wieder in ihrem Zeitfenster — ausgewählt und im Bild.
   *
   * Vorgemerkt statt sofort ausgewählt: In die Liste kommt sie erst mit dem
   * nächsten Anstrich, und `selectTask` findet nur, was schon dasteht.
   * Das Scrollen macht danach `TaskEntry` selbst.
   */
  const navVisibleIds = useMemo(
    () => navigation.flatNavList.map(item => item.id),
    [navigation.flatNavList],
  )
  const markPendingRestore = usePendingRestore(navVisibleIds, navSelectTask)
  const handleRestoreCompleted = useCallback((taskId: string) => {
    markPendingRestore(taskId)
    handleToggleComplete(new Set([taskId]))
  }, [markPendingRestore, handleToggleComplete])

  // Preserve selection + focus per tab (scoped by route pathname) so
  // switching tabs/life spaces and coming back restores the same selection.
  const { pathname } = useLocation()
  useScopedNavState(pathname, {
    focusedNavIndex: navigation.focusedNavIndex,
    selectedIds: navigation.selectedIds,
    // Use the with-anchor setter so shift-select ranges after restore
    // originate from the restored focus index, not wherever anchorRef
    // happens to be on a fresh mount.
    setFocusedNavIndex: navigation.setFocusedNavIndexWithAnchor,
    setSelectedIds: navigation.setSelectedIds,
    clearSelection: navClearSelection,
  })

  // After a successful restore, hand keyboard focus to the list wrapper
  // so Arrow keys work without an extra click. The hook's restore runs on
  // mount, but TaskBoard (which owns wrapperRef) is gated behind
  // projectLoading — so wrapperRef.current is null at that point. We wait
  // until flatNavList has items AND wrapperRef is mounted, then focus once.
  const mountFocusDoneRef = useRef(false)
  useEffect(() => {
    if (mountFocusDoneRef.current) return
    if (navigation.focusedNavIndex === null) return
    const wrapper = navigation.wrapperRef.current
    if (!wrapper || navigation.flatNavList.length === 0) return
    wrapper.focus({ preventScroll: true })
    mountFocusDoneRef.current = true
  }, [navigation.focusedNavIndex, navigation.flatNavList.length, navigation.wrapperRef])

  // Re-focus the wrapper whenever this page becomes active. `display: none`
  // on inactive TabStack slots drops focus to <body>, so without this the
  // user had to click the list once after every tab switch to get keyboard
  // nav back.
  useEffect(() => {
    if (!isActive) return
    const rafId = requestAnimationFrame(() => {
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      navigation.wrapperRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, navigation.wrapperRef])
  const focusedItem = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
  navFocusedItemRef.current = focusedItem ? { type: focusedItem.type, id: focusedItem.id } : null

  // Derive the active segment — target for A/B and ⌘N
  const activeSegmentIndex = useMemo(() => {
    if (navigation.focusedNavIndex !== null) {
      const item = navigation.flatNavList[navigation.focusedNavIndex]
      if (item) {
        const segIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === item.id))
        if (segIdx !== -1) return segIdx
      }
    }
    if (hoveredSegmentIndex !== null && hoveredSegmentIndex < segments.length) {
      return hoveredSegmentIndex
    }
    return Math.min(lastInteractedSegmentIndex, Math.max(0, segments.length - 1))
  }, [hoveredSegmentIndex, navigation.focusedNavIndex, navigation.flatNavList, segments, lastInteractedSegmentIndex])
  activeSegmentRef.current = activeSegmentIndex

  // Track last interacted segment from keyboard navigation
  useEffect(() => {
    if (navigation.focusedNavIndex === null) return
    const item = navigation.flatNavList[navigation.focusedNavIndex]
    if (!item) return
    const segIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === item.id))
    if (segIdx !== -1) setLastInteractedSegmentIndex(segIdx)
  }, [navigation.focusedNavIndex, navigation.flatNavList, segments])

  // Einhängen und Umhängen (#204): Das Ziel klappt auf, man sieht die Unteraufgabe dort landen.
  const { setEditingTaskId: setEditingForNest } = navigation
  const handleExpandForNest = useCallback((taskId: string) => {
    navSelectTask(taskId)
    setEditingForNest(taskId)
  }, [navSelectTask, setEditingForNest])

  // Herausgelöst (Ziehen oder ⇧⌥): die Aufgaben in der Liste ausgewählt, sobald sie dort stehen.
  const { setSelectedIds: setNavSelectedIds } = navigation
  const selectWhenListed = usePendingSelection(navVisibleIds, ids => {
    navSelectTask(ids[0]!)
    if (ids.length > 1) setNavSelectedIds(new Set(ids))
  })
  // Wer mit Wann oder Ruhe herauskommt, steht nie hier — die Leiste sagt, wohin er ging.
  const markPendingSelection = useCallback((ids: string[]) => {
    selectWhenListed(ids)
    noteUnnested(ids)
  }, [selectWhenListed, noteUnnested])

  // ── Drag-and-drop ──
  // Cross-segment drag is blocked: virtual sections are computed from createdAt,
  // a task dragged from "Älter" to "Letzte 7 Tage" would snap back on the next render.
  const {
    activeDragId, draggedIds, dragCount, isCrossSegmentDrag, gapAfterSegmentIdx, gapHeight,
    crossSegGapAfterRef, crossSegGapAfter, nestTarget, nestLatchRef, subtaskDragOut, insertLine, leaveList,
    sensors, handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
  } = useTaskDragAndDrop({
    segments, tasks, sections: noSections, taskListRef, navSelectTask, setOptimisticUpdates,
    allowCrossSegmentDrag: false,
    onExpandTask: handleExpandForNest,
    onSelectTasks: markPendingSelection,
  })
  // Per Tastatur und Menü verlässt die Zeile die Liste wie beim Ziehen: Ihre Stelle fährt zu.
  const { nest: nestByCommand } = useNestActions({ onExpandTask: handleExpandForNest, onLeave: leaveList })
  // ⇧⌥↑ hängt hinten in die Aufgabe darüber (man kommt von unten), ⇧⌥↓ vorn in die darunter.
  nestSelectionRef.current = (ids, parentId, direction) => { void nestByCommand(ids, parentId, { at: direction === -1 ? 'end' : 'start' }) }
  moveSelectionRef.current = useKeyboardMove({ segments, setOptimisticUpdates, crossSections: false })
  const nestMenu = useNestMenu({
    segments, selectedIds: navigation.selectedIds, nest: nestByCommand, untitled: t('taskList.nest.untitled'),
    selectInList: markPendingSelection,
  })

  // Section drag is a no-op (no real sections); wire the hook anyway so TaskBoard props line up.
  const {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart,
    handleSectionDragOver,
    handleSectionDragEnd,
    handleSectionDragCancel,
  } = useSectionDrag({
    segments, tasks, sections: noSections,
    wrapperRef: taskListRef,
    setOptimisticUpdates, setOptimisticSectionUpdates,
  })
  void hoverTargetIdx
  void segmentRectsRef
  void sourceHeaderHeightRef
  void optimisticSectionUpdates

  useSectionDragAnimation({
    wrapperRef: taskListRef,
    activeSectionDragId,
    hoverTargetIdx,
    segments,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
  })

  isDragAnimatingRef.current = activeDragId !== null || activeSectionDragId !== null

  const [sectionDragYOffset, setSectionDragYOffset] = useState(0)
  useLayoutEffect(() => {
    if (!activeSectionDragId) {
      setSectionDragYOffset(0)
      return
    }
    const headerEl = document.querySelector<HTMLElement>(`.section-header[data-section-id="${activeSectionDragId}"]`)
    if (headerEl) {
      setSectionDragYOffset(headerEl.getBoundingClientRect().top - sourceHeaderTopRef.current)
    }
  }, [activeSectionDragId, sourceHeaderTopRef])

  const excludeId = morphData || inlineMorphTitle ? autoSelectId ?? undefined : undefined
  const {
    composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
    customCollisionDetection, allSortableIds, segmentAwareStrategy,
    sectionDragWidth, dragRotation, dragSourceSegIdx,
  } = useDndComposition({
    segments, excludeId,
    handleSectionDragStart, handleSectionDragOver, handleSectionDragEnd, handleSectionDragCancel,
    activeSectionDragId,
    handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
    crossSegGapAfterRef, crossSegGapAfter, nestLatchRef,
    onDragFinish: navigation.restoreListFocus,
  })

  const handleHoverLeave = useCallback(() => {
    setHoveredSegmentIndex(null)
  }, [])

  /*
    Die **Listen-Werkzeug-Pille** hier nur mit „Neue Aufgabe" (#229): keine
    Abschnitte, keine Pinnwand. Das Ziel ist immer oben in „Letzte 7 Tage" —
    die neueste Notiz zuerst (#247, `quickNotesNewTaskTarget`). ⌘N und das Menü
    ohne Fokus nehmen denselben Weg.
  */
  const handleListToolbarAddTask = useCallback(() => {
    const { position, segmentIndex, expandSectionId } = quickNotesNewTaskTarget(segments)
    if (expandSectionId) toggleCollapsed(expandSectionId)
    startCreating(position, segmentIndex)
  }, [segments, toggleCollapsed, startCreating])

  /*
    **„Neue Aufgabe“ in die Liste ziehen** (#301) — hier nur innerhalb von „Letzte 7 Tage“: Eine neue
    Notiz ist immer jung, und zwischen den Gruppen wird ohnehin nicht verschoben. Fehlt die Gruppe
    noch, gibt es genau ein Ziel ganz oben, wo der Klick anlegt (`quickNotesNewTaskTarget`).
  */
  const recentIndex = segments.findIndex(segment => segment.section?.id === QUICK_NOTES_SECTION_IDS.recent)
  const acceptsNewTask = useCallback((segmentIndex: number) => (
    recentIndex === -1 ? (segmentIndex === 0 ? 'top' as const : 'none' as const)
      : segmentIndex === recentIndex ? 'all' as const : 'none' as const
  ), [recentIndex])
  const handleNewTaskDrop = useCallback((target: NewTaskDropTarget) => applyNewTaskDrop(target, {
    startCreating, startCreatingAfter, expand: sectionId => { toggleCollapsed(sectionId) },
  }), [startCreating, startCreatingAfter, toggleCollapsed])
  const newTaskDrag = useNewTaskDrag({
    isActive,
    scrollerRef: bodyMainRef,
    listRef: taskListRef,
    accepts: acceptsNewTask,
    onDrop: handleNewTaskDrop,
    edgeInsets: { top: 0, bottom: LIST_TOOLBAR_CLEARANCE },
  })
  // Nach einem Zug kommt der Klick des Buttons noch an — er legt nicht ein zweites Mal an.
  const { consumeClick: consumeNewTaskClick } = newTaskDrag
  const handleListToolbarAddTaskClick = useCallback(() => {
    if (!consumeNewTaskClick()) handleListToolbarAddTask()
  }, [consumeNewTaskClick, handleListToolbarAddTask])

  // ── Duplicate items ──
  // Pass `effectiveTasks` (override-merged) so that a duplicate fired
  // immediately after a status-toggle clones the toggled state — not the
  // stale Dexie row that's still mid-LiveQuery-refire.
  const { handleDuplicate } = useDuplicateItems({
    segments, tasks: effectiveTasks, sections: noSections,
    selectedIds: navigation.selectedIds,
    flatNavList: navigation.flatNavList,
    focusedNavIndex: navigation.focusedNavIndex,
    setSelectedIds: navigation.setSelectedIds,
    setFocusedNavIndexWithAnchor: navigation.setFocusedNavIndexWithAnchor,
  })

  const handleDuplicateFromContextMenu = useCallback((taskId: string) => {
    const selected = navigation.selectedIds
    if (selected.size > 1 && selected.has(taskId)) {
      handleDuplicate()
    } else {
      handleDuplicate([taskId])
    }
  }, [handleDuplicate, navigation.selectedIds])

  const applyBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => navigation.selectedIds,
    setOptimisticTaskOverride,
    logTag: 'QuickNotesPage',
  })

  /*
    **Dieselbe Tür für die Meldung** (Abnahme F-KN3): Jede Änderung aus Menü,
    Marke oder aufgeklappter Karte läuft hier durch. Welche Aufgaben gemeint
    sind, entscheidet dieselbe Regel wie in `useContextMenuBadgeUpdate` — eine
    Mehrfachauswahl zählt nur, wenn die angeklickte Aufgabe dazugehört.
  */
  const handleContextMenuBadgeUpdate = useCallback((taskId: string, updates: Partial<Omit<Task, 'id'>>) => {
    const auswahl = navigation.selectedIds
    const ziele = auswahl.size > 1 && auswahl.has(taskId) ? [...auswahl] : [taskId]
    // Erst anwenden: `applyBadgeUpdate` liest die Auswahl selbst, und die wird
    // gleich darauf aufgehoben.
    applyBadgeUpdate(taskId, updates)
    /*
      **Was die Liste verlässt, ist nicht mehr ausgewählt.** Sonst bliebe die
      Markierung an einer Zeile hängen, die es hier nicht mehr gibt — und die
      nächste Taste träfe eine Aufgabe, die der Blick schon verloren hat.
    */
    if (noteUpdate(ziele, updates) > 0) navClearSelectionRef.current?.()
  }, [applyBadgeUpdate, navigation.selectedIds, noteUpdate])

  /* Die Haken im **Aufgaben-Menü** — dieselbe Auswahl wie oben. */
  const getContextMenuMarks = useContextMenuMarks({
    getSelection: () => navigation.selectedIds,
    tasks: tasks ?? [],
  })

  // ── Keyboard shortcuts ──
  useProjectKeyboardShortcuts({
    savedName: t('quickNotes.title'),
    tasks, creatingPosition, creatingAfterTaskId, segments,
    activeSegmentRef, navClearSelection, navSelectTask,
    startCreating, startCreatingAfter,
    onAddSection: () => { /* no-op: no sections on Quick Notes */ },
    handleDuplicate,
    focusedNavIndex: navigation.focusedNavIndex,
    flatNavList: navigation.flatNavList,
    sectionsReadonly: true,
    isActive,
    onNewTask: handleListToolbarAddTask,
  })

  /*
    **Die Kürzel des Aufgaben-Menüs** — dieselben wie auf der Projektseite.
    Eigener Hook, weil sie für jede Seite mit einer Aufgabenliste gelten und
    nicht nur dort, wo man Aufgaben anlegt.
  */
  const [menuShortcut, setMenuShortcut] = useState<{ taskId: string; target: TaskMenuTarget } | null>(null)
  const closeMenuShortcut = useCallback(() => setMenuShortcut(null), [])
  // „Verschieben …“ aus dem Rechtsklick öffnet denselben Kasten an der Zeile wie ⇧⌘M (#263).
  const openMoveAtRow = useCallback((taskId: string) => setMenuShortcut({ taskId, target: 'move' }), [])

  const handleRestShortcut = useCallback((taskId: string) => {
    const task = tasks?.find(entry => entry.id === taskId)
    if (!task) return
    handleContextMenuBadgeUpdate(taskId, task.pausedAt ? wakeTaskUpdate(task, new Date()) : { pausedAt: new Date() })
  }, [tasks, handleContextMenuBadgeUpdate])

  // ⇧⌘F (#210): der Stern der Auswahl — mit Meldung und Rückgängig.
  const favoriteShortcut = useFavoriteShortcut({ getSelection: () => navigation.selectedIds, applyUpdate: applyBadgeUpdate })
  useTaskMenuShortcuts({
    // Im Klär-Ablauf ist die Liste weg, ihr Anker aber nicht: D, P & Co. träfen sonst die zuletzt
    // markierte Zeile, P startete dort einen Fokus (#266). Dort hat jede Karte ihre eigenen Tasten.
    enabled: isActive && !clarifyMode,
    getAnchorTaskId: () => {
      const focused = navigation.focusedNavIndex !== null
        ? navigation.flatNavList[navigation.focusedNavIndex]
        : null
      return anchorTaskId(
        focused?.type === 'task' ? focused.id : null,
        navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
        navigation.selectedIds,
      )
    },
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onFavoriteShortcut: taskId => { if (taskId) void favoriteShortcut(taskId) },
    onRestShortcut: handleRestShortcut,
    onFocusShortcut: handlePlayTask,
    onDetailsShortcut: details.toggle,
    canMove: true,
  })

  // **Verschieben …** (#257) — hier heißt das: einsortieren, in ein Projekt jedes Lebensbereichs.
  const moveMenu = useMoveMenu(() => navigation.selectedIds, openMoveAtRow)

  // **Die Befehle der Auswahl in der Pille** (#291) — wie im Projekt.
  const taskUpdates = useTaskUpdates({ setOptimisticTaskOverride, logTag: 'QuickNotesPage' })
  const listedTaskIds = useMemo(
    () => navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
    [navigation.flatNavList],
  )
  const renderSelection = useSelectionActions({
    selectedIds: navigation.selectedIds,
    orderedTaskIds: listedTaskIds,
    tasks: effectiveTasks,
    updates: taskUpdates,
    toggleComplete: toggleCompleteWithGrace,
    deleteTasks: (ids) => { void handleDeleteTask(ids) },
    duplicateTasks: (ids) => { void handleDuplicate(ids) },
    clearSelection: navClearSelection,
    moveMenu,
    nestMenu,
    // Dieselbe Tür wie `handleContextMenuBadgeUpdate` (F-KN3): Was mit einem Startdatum geht, wird gemeldet und
    // ist nicht mehr ausgewählt — sonst träfe ⌫ später unsichtbar die nach Demnächst gezogenen.
    onWritten: (ids, changes) => { if (noteUpdate(ids, changes) > 0) navClearSelectionRef.current?.() },
    restoreListFocus: navigation.restoreListFocus,
  })


  /*
    Die Klassen der **Projektseite** tragen das Nebeneinander von Liste und
    Karte (`ProjectPage.scss`, das diese Seite ohnehin lädt) — samt der
    Bewegung beim Auf- und Zugehen. Im **Klär-Ablauf** bleibt die Spalte weg:
    Dort steht eine Karte nach der anderen, daneben ist nichts.
  */
  const detailsOpen = details.open && !clarifyLayoutActive
  const detailsVisible = details.visible && !clarifyLayoutActive

  /*
    **Das Menü „Aufgaben“ in der Menüleiste** (#257). „Im Projekt zeigen“ fehlt: Was hier steht, hat
    noch kein Projekt. Im Klär-Ablauf gilt die Liste nicht — dort hat jede Karte ihre eigenen Tasten.
  */
  const menuAnchor = useMemo(() => {
    const focused = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
    const id = anchorTaskId(
      focused?.type === 'task' ? focused.id : null,
      navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
      navigation.selectedIds,
    )
    return menuAnchorOf(id ? effectiveTasks?.find(task => task.id === id) : null, effectiveTasks)
  }, [navigation.focusedNavIndex, navigation.flatNavList, navigation.selectedIds, effectiveTasks])
  useAppTaskMenu({
    enabled: isActive && !clarifyMode,
    anchor: menuAnchor,
    multiSelected: navigation.selectedIds.size > 1,
    detailsOpen,
    canMove: true,
    onMenuShortcut: (taskId, target) => setMenuShortcut({ taskId, target }),
    onComplete: navigation.completeSelection,
    onFocus: handlePlayTask,
    onRest: handleRestShortcut,
    onFavorite: taskId => { void favoriteShortcut(taskId) },
    onToggleDetails: details.toggle,
    onDuplicate: () => handleDuplicate(),
    onDelete: navigation.deleteSelection,
  })

  /*
    **Leerer Zustand wie im leeren Projekt** (25.09.2026). Nur, solange gerade
    nichts entsteht: Ist der Ersteller offen oder läuft sein Morph in die
    fertige Zeile, braucht es das Brett — sonst verschwände der Ersteller
    (dieselben Gründe wie `shouldShowCanvasEmptyState` im Projekt). Steht die
    „weitergezogen"-Meldung, gehört sie auf die Liste, nicht über einen leeren
    Zustand.
  */
  const showEmptyState =
    tasks !== undefined && tasks.length === 0 &&
    creatingPosition === null && creatingAfterTaskId === null &&
    !morphData && positionMorphKey == null && held.length === 0
  // Die Listen-Werkzeug-Pille — nicht im Klären und nicht über dem leeren Zustand.
  const showListToolbar = !clarifyMode && !showEmptyState && tasks !== undefined
  // Leer steht kein Kopf über dem leeren Zustand. Der Name steht in der
  // Breadcrumb — leer fest, sonst sobald die Überschrift weggerollt ist, wie auf
  // Ruhend, Geschafft und Wiederkehrend (#273). Dafür hat Kurz notiert dort keine
  // Gruppe mehr (`getSectionTitle`): in Ruhe nur „Arbeit“, nicht „Arbeit › Kurz
  // notiert“ (bis 06.10.2026).
  usePageBreadcrumb({
    title: t('quickNotes.title'), isActive, loaded: tasks !== undefined, isEmpty: showEmptyState,
    titleRef, scrollerRef: bodyMainRef,
  })

  return (
    <TitleRecognitionScope lifeSpace={lifeSpace}>
    <TaskDetailsOpenerProvider value={details.opener}>
    <MoveMenuContext.Provider value={moveMenu}>
    <div
      ref={pageRef}
      className={`task-view quick-notes-page project-page${clarifyLayoutActive ? ' quick-notes-page--clarify-mode' : ''}${detailsOpen ? ' project-page--details-open' : ''}${detailsVisible && !detailsOpen ? ' project-page--details-closing' : ''}${detailsVisible ? ' quick-notes-page--details-column' : ''}${showEmptyState ? ' project-page--empty' : ''}`}
    >
    <div className="project-page__body quick-notes-page__body">
    <div ref={listColumnRef} className="project-page__list-column">
    <div
      ref={bodyMainRef}
      className={`project-page__body-main${scrollbarVisible ? ' project-page__body-main--scroll-visible' : ''}`}
      onScroll={handleListScroll}
    >
      {/* Erst mit Daten: Im ersten Bild sind die Aufgaben noch nicht geladen —
          der Kopf blitzte dort auf und verschwand, sobald der leere Zustand
          kam (25.09.2026). */}
      {tasks !== undefined && !showEmptyState && (
        <div className="quick-notes-page__header">
          <div className="quick-notes-page__header-actions">
            {/* Ohne eine einzige Aufgabe gibt es nichts zu zeigen — der Knopf wäre
                ohne Wirkung (25.09.2026, wie im leeren Projekt). Offen bleibt er
                stehen, damit man wieder herauskommt. */}
            {!clarifyLayoutActive && ((tasks?.length ?? 0) > 0 || detailsOpen) && (
              <DetailsToggle open={detailsOpen} onToggle={details.toggle} />
            )}
          </div>
          <div className="page-help__row">
            <h1 ref={titleRef} className="quick-notes-page__title">
              {(tasks?.length ?? 0) > 0
                ? <QuickNotesFilledOpenIcon size={18} className="quick-notes-page__title-icon" />
                : <QuickNotesEmptyOpenIcon size={18} className="quick-notes-page__title-icon" />}
              {t('quickNotes.title')}
            </h1>
            <PageHelpButton page="quickNotes" />
          </div>
          <p className="quick-notes-page__subtitle">{t('quickNotes.subtitle')}</p>
        </div>
      )}

      <AnimatePresence mode="wait" initial={false} onExitComplete={handleClarifyExitComplete}>
        {clarifyMode && clarifyTasks ? (
          <motion.div
            key="clarify"
            className="quick-notes-page__content-group"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ClarifySession
              tasks={clarifyTasks}
              lifeSpace={lifeSpace}
              onExit={exitClarify}
              isActive={isActive}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            className="quick-notes-page__content-group"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* Einführung (#323): Lernaufgaben auf Angebot — über der Liste; im leeren Zustand an Stelle der Buttons. */}
            {tasks !== undefined && !showEmptyState && <IntroOfferCard lifeSpace={lifeSpace} />}
            {tasks && tasks.length > 0 && (
              <div className="quick-notes-page__clarify-bar">
                <button
                  type="button"
                  className="button button--outline button--size-md quick-notes-page__clarify-button"
                  onClick={toggleClarify}
                >
                  {/* Dasselbe Symbol wie im Kopf des Einsortierens (Sven, 06.10.2026). */}
                  <span className="button__icon"><Combine size={16} /></span>
                  <span className="button__text">{t('quickNotes.clarifyEntries')}</span>
                  <span className="quick-notes-page__clarify-button-hint">
                    <ShortcutHint variant="emphasized">K</ShortcutHint>
                  </span>
                </button>
              </div>
            )}
            {/*
              Die Meldung macht Platz, statt dazustehen: `AutoHeight` fährt ihre
              gemessene Höhe auf, die Liste rückt mit. `growMotion` wäre das
              Gegenstück für eine feste Größe — hier hängt sie daran, wie viele
              Aufgaben gegangen sind.
            */}
            <AnimatePresence initial={false}>
              {held.length > 0 && (
                <AutoHeight appear className="quick-notes-page__moved-notice" bodyClassName="quick-notes-page__moved-notice-body">
                  <InboxMovedNotice tasks={held} onConfirm={confirmMoved} onTaskUpdate={updateHeld} />
                </AutoHeight>
              )}
            </AnimatePresence>
            {showEmptyState ? (
              // Erst wenn klar ist, ob das Angebot offen ist: Es steht an Stelle der Buttons, die sonst aufblitzten.
              introOffer !== undefined && (
                <QuickNotesEmptyState
                  onCreateTask={() => startCreating('top', 0)}
                  {...emptyAppActions}
                  offer={introOffer === 'pending' ? <IntroOfferCard lifeSpace={lifeSpace} layout="stack" /> : undefined}
                />
              )
            ) : (
            /*
              Abgeschirmt vom `AnimatePresence initial={false}` oben: Das gilt dem
              Überblenden Liste ↔ Einsortieren, schaltete aber auch die
              Einblendung der neuen Zeile im Brett ab — nach „Neue Aufgabe
              erstellen …“ stand sie hier sofort da, im Projekt fährt sie auf
              (25.09.2026). So verhält sich das Brett wie im Projekt.
            */
            <PresenceContext.Provider value={null}>
              <div className="quick-notes-page__mode-slot">
                <TaskBoard
          segments={segments}
          tasks={tasks}
          projectId={undefined}
          gapAfterSegmentIdx={gapAfterSegmentIdx}
          gapHeight={gapHeight}
          activeDragId={activeDragId}
          draggedIds={draggedIds}
          subtaskDragOut={subtaskDragOut}
          insertLine={insertLine}
          nestMenu={nestMenu}
          nestTarget={nestTarget}
          dragCount={dragCount ?? 0}
          isCrossSegmentDrag={isCrossSegmentDrag}
          activeSectionDragId={activeSectionDragId}
          dragSourceSegIdx={dragSourceSegIdx}
          sectionDragWidth={sectionDragWidth}
          dragRotation={dragRotation}
          sectionDragYOffset={sectionDragYOffset}
          taskListRef={taskListRef}
          taskListHandleRef={taskListHandleRef}
          getSegmentHandleRef={getSegmentHandleRef}
          sensors={sensors}
          customCollisionDetection={customCollisionDetection}
          allSortableIds={allSortableIds}
          segmentAwareStrategy={segmentAwareStrategy}
          composedDragStart={composedDragStart}
          composedDragMove={composedDragMove}
          composedDragOver={composedDragOver}
          composedDragEnd={composedDragEnd}
          composedDragCancel={composedDragCancel}
          navigation={navigation}
          hoveredSegmentIndex={hoveredSegmentIndex}
          setHoveredSegmentIndex={setHoveredSegmentIndex}
          creatingSegmentIdx={creatingSegmentIdx}
          creatingPosition={creatingPosition}
          creatingAfterTaskId={creatingAfterTaskId}
          pendingInlineTarget={pendingInlineTarget}
          creationKey={creationKey}
          creationLanded={creationLanded}
          newTaskDrop={newTaskDrag.drop}
          inlineResetKey={inlineResetKey}
          skipInlineEntrance={skipInlineEntrance}
          autoSelectId={autoSelectId}
          morphData={morphData}
          positionMorphKey={positionMorphKey}
          inlineMorphTitle={inlineMorphTitle}
          inlineMorphSelected={inlineMorphSelected}
          inlineMorphKey={inlineMorphKey}
          topCreateRef={topCreateRef}
          bottomCreateRef={bottomCreateRef}
          handleCreateTask={handleCreateTask}
          handleCreateTaskAndContinue={handleCreateTaskAndContinue}
          handleCreateTaskAfter={handleCreateTaskAfter}
          handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
          handleCancelCreate={handleCancelCreate}
          handleMorphComplete={handleMorphComplete}
          handleInlineMorphComplete={handleInlineMorphComplete}
          autoEditSectionId={null}
          handleToggleCollapse={toggleCollapsed}
          handleRenameSection={() => { /* no-op: virtual sections */ }}
          handleDeleteSection={() => { /* no-op: virtual sections */ }}
          handleToggleComplete={toggleCompleteWithGrace}
          handleDeleteTask={handleDeleteTask}
          handleUpdateTask={handleUpdateTask}
          handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
          handleDuplicate={handleDuplicateFromContextMenu}
          handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
          getContextMenuMarks={getContextMenuMarks}
          menuShortcut={menuShortcut}
          onPlay={handlePlayTask}
          onMenuShortcutClose={closeMenuShortcut}
          optimisticUpdates={optimisticUpdates}
          handleHoverLeave={handleHoverLeave}
          handleClearSelection={navClearSelection}
          sectionsReadonly={true}
          allowCrossSegmentDrag={false}
          completedToggle={(
            <CompletedToggle
              tasks={completedTasks}
              open={completedOpen}
              stateKey={`quick-notes-done-${lifeSpace}`}
              onOpenChange={setCompletedOpen}
              onRestore={handleRestoreCompleted}
              onDelete={taskId => handleDeleteTask(new Set([taskId]))}
              externalSelection={navigation.getExternalSelection(completedTasks.map(task => task.id))}
              onNavigateEdit={navigation.navigateEdit}
              onUpdateTask={handleUpdateTask}
            />
          )}
        />
              </div>
            </PresenceContext.Provider>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
      {/* Außerhalb des Scrollers, schwebt über der Liste — wie im Projekt. */}
      {showListToolbar && (
        <ListToolbarDock
          isActive={isActive}
          columnRef={listColumnRef}
          canvasAvailable={false}
          canvasOpen={false}
          sectionsAllowed={false}
          // Taucht ab, solange ein Ersteller offen ist (#229) oder „Neue Aufgabe“ gezogen wird — wie im Projekt.
          retracted={isListToolbarRetracted({
            creatingTask: creatingPosition !== null || creatingAfterTaskId !== null,
            namingSection: false,
            placingPaper: false,
            draggingFromPill: newTaskDrag.drop !== null,
          })}
          onAddTask={handleListToolbarAddTaskClick}
          onAddTaskPointerDown={newTaskDrag.onAddTaskPointerDown}
          addTaskDragging={newTaskDrag.dragging}
          renderSelection={renderSelection}
        />
      )}
      {newTaskDrag.ghost && <NewTaskDragGhost {...newTaskDrag.ghost} />}
    </div>
      {detailsVisible && (
        <TaskDetails
          tasks={details.tasks}
          openSubtask={details.subtaskRequest}
          openComments={details.commentsRequest}
          openReminders={details.remindersRequest}
          focusedSeconds={details.focusedSeconds}
          onUpdate={handleContextMenuBadgeUpdate}
          onClose={details.close}
          onStepTask={details.stepTask}
          onRest={details.onRest}
          canStepPrev={details.canStepPrev}
          canStepNext={details.canStepNext}
          // Escape darin gibt die Tastatur an die Liste zurück und schließt
          // NICHT: Der Ausgang ist das Kreuz (CONTEXT.md).
          onReleaseFocus={details.releaseFocusToList}
          focusTitleSignal={details.focusTitleSignal}
        />
      )}
    </div>
      {/* Schein am unteren Rand, hinter den Knöpfen im Baum (der Anker muss vorher kommen). */}
      {showEmptyState && <EmptyStateGlow className="project-page__glow" />}
    </div>
    </MoveMenuContext.Provider>
    </TaskDetailsOpenerProvider>
    </TitleRecognitionScope>
  )
}
